import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import ConsentModal from '../../components/ConsentModal/ConsentModal';
import { recordLoyaltyConsent } from '../../services/publicUserApi';
import './LoyaltyAuth.css';

import { API_BASE_URL, RECAPTCHA_SITE_KEY } from '../../config/env';
import { isLoyaltyRecaptchaSkipped } from '../../utils/devOverrides';
import { loyaltySession } from '../../utils/session';
import { redirectToLoyaltyLanding, clearLoyaltyLogoutMarker } from '../../utils/loyaltySessionRestore';
import LoyaltySessionGate from '../../components/LoyaltySessionGate/LoyaltySessionGate';
import { FETCH_CREDENTIALS } from '../../utils/laravelClient';
import { extractAccessToken } from '../../utils/tokenHelpers';

function LoyaltyAuthForm() {
    const { t, i18n } = useTranslation();
    const [tab, setTab] = useState(0); // 0 = login, 1 = register
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { executeRecaptcha } = useGoogleReCaptcha();

    // Check for deletion success message
    useEffect(() => {
        if (location.state?.message) {
            setSuccess(location.state.message);
            // Clear the location state
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location, navigate]);

    // Login form state
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');

    // Registration form state
    const [registerName, setRegisterName] = useState('');
    const [registerPhone, setRegisterPhone] = useState('');
    const [registerEmail, setRegisterEmail] = useState('');
    const [registerPassword, setRegisterPassword] = useState('');
    const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
    const [preferredLanguage, setPreferredLanguage] = useState('bg'); // Default to Bulgarian

    // Consent modal state
    const [showConsentModal, setShowConsentModal] = useState(false);
    const [consentGiven, setConsentGiven] = useState(false);

    const handleTabChange = (event, newValue) => {
        setTab(newValue);
        setError(null);
        setSuccess(null);
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!loginEmail || !loginPassword) {
            setError(t('loyalty.auth.errors.fillAllFields'));
            return;
        }

        const skipRecaptcha = isLoyaltyRecaptchaSkipped(API_BASE_URL);

        if (!skipRecaptcha && !executeRecaptcha) {
            setError('reCAPTCHA not loaded');
            return;
        }

        setLoading(true);

        try {
            let payload = {
                email: loginEmail,
                password: loginPassword,
            };

            if (skipRecaptcha) {
                payload.skip_recaptcha = true;
            } else {
                const recaptchaToken = await executeRecaptcha('loyalty_login');
                payload.recaptcha_token = recaptchaToken;
            }


            const response = await fetch(`${API_BASE_URL}/loyalty/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: FETCH_CREDENTIALS,
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                let errorMessage = data.message || 'Login failed';

                // Check if email is not verified
                if (data.message && data.message.toLowerCase().includes('verify')) {
                    errorMessage = t('loyalty.auth.errors.emailNotVerified') ||
                        'Please verify your email before logging in. Check your inbox for the verification link.';

                    // Optionally show resend button
                    setError(errorMessage);
                    setLoading(false);
                    return;
                }

                // Add retry time if rate limited
                if (data.retry_after_minutes) {
                    errorMessage += ` ${t('loyalty.auth.errors.retryAfter', 'Please try again in {{minutes}} minutes', { minutes: data.retry_after_minutes })}`;
                }

                throw new Error(errorMessage);
            }

            const data = await response.json();
            const bearerToken = extractAccessToken(data);

            loyaltySession.setSession(data.user, { bearerToken: bearerToken || undefined });
            clearLoyaltyLogoutMarker();

            // Set language based on user's preferred language
            if (data.user && data.user.preferred_language) {
                i18n.changeLanguage(data.user.preferred_language);
            }

            setSuccess(t('loyalty.auth.success.loginSuccess'));

            setTimeout(() => {
                redirectToLoyaltyLanding(location.search);
            }, 1000);
        } catch (err) {
            console.error('Login error:', err);
            setError(err.message || t('loyalty.auth.errors.loginFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!registerName || !registerPhone || !registerEmail || !registerPassword || !registerConfirmPassword) {
            setError(t('loyalty.auth.errors.fillAllFields'));
            return;
        }

        if (registerPassword !== registerConfirmPassword) {
            setError(t('loyalty.auth.errors.passwordMismatch'));
            return;
        }

        if (registerPassword.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        // Show consent modal before proceeding
        setShowConsentModal(true);
    };

    const handleConsentAccept = async () => {
        setShowConsentModal(false);
        setError(null);
        setSuccess(null);

        const skipRecaptcha = isLoyaltyRecaptchaSkipped(API_BASE_URL);

        if (!skipRecaptcha && !executeRecaptcha) {
            setError('reCAPTCHA not loaded');
            return;
        }

        setLoading(true);

        try {
            let payload = {
                name: registerName,
                phone: registerPhone,
                email: registerEmail,
                password: registerPassword,
                password_confirmation: registerConfirmPassword,
                preferred_language: preferredLanguage,
                consent: true, // User explicitly gave consent through modal
            };

            if (skipRecaptcha) {
                payload.skip_recaptcha = true;
            } else {
                const recaptchaToken = await executeRecaptcha('loyalty_register');
                payload.recaptcha_token = recaptchaToken;
            }

            const response = await fetch(`${API_BASE_URL}/loyalty/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: FETCH_CREDENTIALS,
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                let errorMessage = data.message || 'Registration failed';

                // Add retry time if rate limited
                if (data.retry_after_minutes) {
                    errorMessage += ` ${t('loyalty.auth.errors.retryAfter', 'Please try again in {{minutes}} minutes', { minutes: data.retry_after_minutes })}`;
                }

                throw new Error(errorMessage);
            }

            const data = await response.json();

            // Record consent using the httpOnly session cookie set by registration
            try {
                await recordLoyaltyConsent();
            } catch (consentErr) {
                console.warn('⚠️ Consent request failed:', consentErr);
                // Don't fail registration if consent endpoint fails
            }

            // Show success message with email verification notice
            setSuccess(
                t('loyalty.auth.success.registrationWithVerification') ||
                'Registration successful! Please check your email to verify your account before logging in.'
            );

            // Clear registration form
            setRegisterName('');
            setRegisterPhone('');
            setRegisterEmail('');
            setRegisterPassword('');
            setRegisterConfirmPassword('');

            // Don't auto-switch to login tab immediately - let user read the verification message
            // User needs to verify email before they can login
        } catch (err) {
            console.error('Registration error:', err);
            setError(err.message || t('loyalty.auth.errors.registrationFailed'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box className="loyalty-auth-container" data-page="loyalty-auth">
            <Card className="loyalty-auth-card">
                <CardContent>
                    <Typography variant="h4" className="loyalty-auth-title">
                        {t('loyalty.auth.title')}
                    </Typography>
                    <Typography variant="body2" className="loyalty-auth-subtitle">
                        {t('loyalty.auth.subtitle')}
                    </Typography>

                    <Tabs
                        value={tab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        sx={{
                            mb: 3,
                            '& .MuiTab-root': {
                                color: 'rgba(255,255,255,0.6)',
                                fontWeight: 600,
                            },
                            '& .Mui-selected': {
                                color: '#ff6b00',
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#ff6b00',
                            },
                        }}
                    >
                        <Tab label={t('loyalty.auth.login')} />
                        <Tab label={t('loyalty.auth.register')} />
                    </Tabs>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}

                    {tab === 0 ? (
                        // Login Form
                        <form onSubmit={handleLogin}>
                            <TextField
                                label={t('loyalty.auth.email')}
                                type="email"
                                fullWidth
                                variant="outlined"
                                margin="normal"
                                value={loginEmail}
                                onChange={(e) => setLoginEmail(e.target.value)}
                                disabled={loading}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#1f1f1f',
                                        color: '#fff',
                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
                                        '&.Mui-focused fieldset': { borderColor: '#ff6b00' },
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                                }}
                            />
                            <TextField
                                label={t('loyalty.auth.password')}
                                type={showPassword ? 'text' : 'password'}
                                fullWidth
                                variant="outlined"
                                margin="normal"
                                value={loginPassword}
                                onChange={(e) => setLoginPassword(e.target.value)}
                                disabled={loading}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                                sx={{ color: 'rgba(255,255,255,0.6)' }}
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#1f1f1f',
                                        color: '#fff',
                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
                                        '&.Mui-focused fieldset': { borderColor: '#ff6b00' },
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                                }}
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={loading}
                                sx={{
                                    mt: 3,
                                    backgroundColor: '#ff6b00',
                                    color: '#fff',
                                    fontWeight: 700,
                                    py: 1.5,
                                    '&:hover': { backgroundColor: '#ff8533' },
                                }}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : t('loyalty.auth.loginButton')}
                            </Button>

                            {/* Forgot Password Link */}
                            <Box sx={{ mt: 2, textAlign: 'center' }}>
                                <Typography
                                    component="a"
                                    href="/loyalty/forgot-password"
                                    sx={{
                                        color: 'rgba(255,255,255,0.7)',
                                        textDecoration: 'none',
                                        fontSize: '0.875rem',
                                        '&:hover': {
                                            color: '#ff6b00',
                                            textDecoration: 'underline',
                                        },
                                    }}
                                >
                                    {t('loyalty.auth.forgotPassword') || 'Forgot Password?'}
                                </Typography>
                            </Box>
                        </form>
                    ) : (
                        // Registration Form
                        <form onSubmit={handleRegister}>
                            <TextField
                                label={t('loyalty.auth.fullName')}
                                type="text"
                                fullWidth
                                variant="outlined"
                                margin="normal"
                                value={registerName}
                                onChange={(e) => setRegisterName(e.target.value)}
                                disabled={loading}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#1f1f1f',
                                        color: '#fff',
                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
                                        '&.Mui-focused fieldset': { borderColor: '#ff6b00' },
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                                }}
                            />
                            <TextField
                                label={t('loyalty.auth.phone')}
                                type="tel"
                                fullWidth
                                variant="outlined"
                                margin="normal"
                                value={registerPhone}
                                onChange={(e) => setRegisterPhone(e.target.value)}
                                disabled={loading}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#1f1f1f',
                                        color: '#fff',
                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
                                        '&.Mui-focused fieldset': { borderColor: '#ff6b00' },
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                                }}
                            />
                            <TextField
                                label={t('loyalty.auth.email')}
                                type="email"
                                fullWidth
                                variant="outlined"
                                margin="normal"
                                value={registerEmail}
                                onChange={(e) => setRegisterEmail(e.target.value)}
                                disabled={loading}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#1f1f1f',
                                        color: '#fff',
                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
                                        '&.Mui-focused fieldset': { borderColor: '#ff6b00' },
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                                }}
                            />
                            <TextField
                                select
                                label={t('loyalty.auth.preferredLanguage')}
                                fullWidth
                                variant="outlined"
                                margin="normal"
                                value={preferredLanguage}
                                onChange={(e) => setPreferredLanguage(e.target.value)}
                                disabled={loading}
                                SelectProps={{
                                    native: true,
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#1f1f1f',
                                        color: '#fff',
                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
                                        '&.Mui-focused fieldset': { borderColor: '#ff6b00' },
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                                    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.6)' },
                                }}
                            >
                                <option value="bg">{t('loyalty.auth.languages.bulgarian')}</option>
                                <option value="en">{t('loyalty.auth.languages.english')}</option>
                            </TextField>
                            <TextField
                                label={t('loyalty.auth.password')}
                                type={showPassword ? 'text' : 'password'}
                                fullWidth
                                variant="outlined"
                                margin="normal"
                                value={registerPassword}
                                onChange={(e) => setRegisterPassword(e.target.value)}
                                disabled={loading}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                                sx={{ color: 'rgba(255,255,255,0.6)' }}
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#1f1f1f',
                                        color: '#fff',
                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
                                        '&.Mui-focused fieldset': { borderColor: '#ff6b00' },
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                                }}
                            />
                            <TextField
                                label={t('loyalty.auth.confirmPassword')}
                                type={showConfirmPassword ? 'text' : 'password'}
                                fullWidth
                                variant="outlined"
                                margin="normal"
                                value={registerConfirmPassword}
                                onChange={(e) => setRegisterConfirmPassword(e.target.value)}
                                disabled={loading}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                edge="end"
                                                sx={{ color: 'rgba(255,255,255,0.6)' }}
                                            >
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#1f1f1f',
                                        color: '#fff',
                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
                                        '&.Mui-focused fieldset': { borderColor: '#ff6b00' },
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                                }}
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={loading}
                                sx={{
                                    mt: 3,
                                    backgroundColor: '#ff6b00',
                                    color: '#fff',
                                    fontWeight: 700,
                                    py: 1.5,
                                    '&:hover': { backgroundColor: '#ff8533' },
                                }}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : t('loyalty.auth.registerButton')}
                            </Button>
                        </form>
                    )}
                </CardContent>
            </Card>

            {/* Consent Modal */}
            <ConsentModal
                open={showConsentModal}
                onClose={() => setShowConsentModal(false)}
                onAccept={handleConsentAccept}
                consent={consentGiven}
                setConsent={setConsentGiven}
            />
        </Box>
    );
}

export default function LoyaltyAuth() {
    const { t } = useTranslation();
    if (!RECAPTCHA_SITE_KEY || RECAPTCHA_SITE_KEY.trim() === '') {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#181a1b' }}>
                <Alert severity="error">
                    {t('loyalty.auth.recaptchaError') || 'reCAPTCHA site key is not configured. Please add REACT_APP_RECAPTCHA_SITE_KEY to your .env file.'}
                </Alert>
            </Box>
        );
    }

    return (
        <LoyaltySessionGate>
            <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
                <LoyaltyAuthForm />
            </GoogleReCaptchaProvider>
        </LoyaltySessionGate>
    );
}
